import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import './styles/MobileOptimization.css';
import './navigation/Navigation.css';
import { AppProvider, useAppContext } from './context/AppContext';
import {
  NavigationProvider,
  PWANavigationManager,
  useNavigation,
  ROUTES,
} from './navigation';
import logo from './data/newlogo.png';

// Page components
import HomePage from './pages/HomePage';
import CartPage from './pages/CartPage';
import HelpPage from './pages/HelpPage';
import ChatPage from './pages/ChatPage';
import AccountPage from './pages/AccountPage';
import OrdersPage from './pages/OrdersPage';
import OrderTrackingPage from './pages/OrderTrackingPage';
import AddressesPage from './pages/AddressesPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  useNavigation(); // Initialize navigation context

  const {
    currentUser,
    authLoading,
    profileLoading,
    productsLoading,
    hideBottomNav,
  } = useAppContext();

  // Page transition state
  const [transitionClass, setTransitionClass] = useState('');
  const prevPathRef = useRef(location.pathname);
  const navigationIndexRef = useRef(0);
  const pathIndexMap = useRef(new Map());

  // Track navigation direction for transition animation
  useEffect(() => {
    const prevPath = prevPathRef.current;
    const newPath = location.pathname;

    if (prevPath === newPath) return;

    // Assign index to paths to determine direction
    if (!pathIndexMap.current.has(prevPath)) {
      pathIndexMap.current.set(prevPath, navigationIndexRef.current);
    }
    navigationIndexRef.current += 1;
    if (!pathIndexMap.current.has(newPath)) {
      // If going to a path we haven't seen, it's forward
      pathIndexMap.current.set(newPath, navigationIndexRef.current);
    }

    const prevIndex = pathIndexMap.current.get(prevPath);
    const newIndex = pathIndexMap.current.get(newPath);
    const isBack = newIndex < prevIndex;

    // Apply transition class
    setTransitionClass(isBack ? 'page-slide-back' : 'page-slide-forward');

    // Remove class after animation completes
    const timer = setTimeout(() => setTransitionClass(''), 250);

    prevPathRef.current = newPath;
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Determine active bottom tab from current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === ROUTES.HELP) return 'help';
    if (path.startsWith('/account')) return 'account';
    return 'home';
  };
  const activeBottomTab = getActiveTab();

  // Show loading screen during initial auth check or profile loading
  if (authLoading || profileLoading || productsLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <img src={logo} alt="Flash" className="loading-logo" />
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className={`page-content ${transitionClass}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help/chat" element={<ChatPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/account/profile" element={<AccountPage />} />
          <Route path="/account/orders" element={<OrdersPage />} />
          <Route path="/account/orders/:orderId" element={<OrderTrackingPage />} />
          <Route path="/account/addresses" element={<AddressesPage />} />
          <Route path="/account/terms" element={<TermsPage />} />
          <Route path="/account/privacy" element={<PrivacyPage />} />
          {/* Fallback to home */}
          <Route path="*" element={<HomePage />} />
        </Routes>
      </div>

      {/* Bottom Navigation */}
      {location.pathname !== '/cart' && !hideBottomNav && (
        <div className="bottom-nav">
          <button
            className={`bottom-nav-item ${activeBottomTab === 'home' ? 'active' : ''}`}
            onClick={() => navigate(ROUTES.HOME)}
          >
            <svg className="bottom-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            <span className="bottom-nav-label">Home</span>
          </button>
          {currentUser ? (
            <button
              className={`bottom-nav-item ${activeBottomTab === 'account' ? 'active' : ''}`}
              onClick={() => navigate(ROUTES.ACCOUNT)}
            >
              <svg className="bottom-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <span className="bottom-nav-label">Account</span>
            </button>
          ) : (
            <button
              className={`bottom-nav-item ${activeBottomTab === 'help' ? 'active' : ''}`}
              onClick={() => navigate(ROUTES.HELP)}
            >
              <svg className="bottom-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span className="bottom-nav-label">Help</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * App wrapper — provides BrowserRouter, NavigationProvider, and AppProvider
 * to the main app content. This enables history-based navigation
 * and mobile back button handling throughout the PWA.
 */
function App() {
  return (
    <BrowserRouter>
      <NavigationProvider>
        <PWANavigationManager>
          <AppProvider>
            <AppContent />
          </AppProvider>
        </PWANavigationManager>
      </NavigationProvider>
    </BrowserRouter>
  );
}

export default App;
